import uniqBy from "lodash/uniqBy";
import React, { FC, useCallback, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";

import { hideMaterialsFilter } from "../../../../features-config.json";
import { MaterialType } from "../../../types/materials";
import { Button } from "../../buttons/button";
import { CollapsiblePanel } from "../../collapsible-panel";
import { Input, Select } from "../../forms";
import { MaterialsFormFields } from "../../forms/forms-fields/materials-form-fields";
import { Icon } from "../../icon";
import { Event, EventsFiltersForm, EventsLabels, EventsTimeInterval } from "../types";
import { EventTypeFilter } from "./event-type-filter";

export interface EventsFiltersProps {
  loading?: boolean;
  timeIntervalEvents: Event[];
  labels: EventsLabels;
  onSubmit: (filters: EventsFiltersForm) => void;
}

export const EventsFilters: FC<EventsFiltersProps> = ({
  labels,
  loading,
  onSubmit,
  timeIntervalEvents,
}) => {
  const [open, setOpen] = useState(false);
  const { watch, register, setValue, handleSubmit } = useFormContext<EventsFiltersForm>();
  const { eventType } = watch();
  const formRef = useRef<HTMLFormElement>(null);

  const submitCallback = useCallback(
    (filters: EventsFiltersForm) => {
      setOpen(false);
      onSubmit(filters);
    },
    [onSubmit]
  );

  const countryOptions = uniqBy(timeIntervalEvents, "country").map((e) => e.country);

  return (
    <form ref={formRef} onSubmit={handleSubmit(submitCallback)}>
      <div className="flex items-center mb-4">
        <Button variant="primary" size="sm" disabled={loading} onClick={() => setOpen((o) => !o)}>
          {labels["events-filters-toggle-button"]}
          <Icon icon="filter" className="inline ml-2" />
        </Button>
        <div className="ml-auto">
          <Select disabled={loading} {...register("timeInterval")}>
            <option value={EventsTimeInterval.FUTURE_EVENTS}>
              {labels["events-filters-show-current-future"]}
            </option>
            <option value={EventsTimeInterval.PAST_EVENTS}>
              {labels["events-filters-show-past"]}
            </option>
          </Select>
        </div>
      </div>
      <CollapsiblePanel isOpen={open}>
        <div className="rounded-3xl p-6 lg:p-8 bg-neutral-light w-full relative">
          <button className="absolute right-3 top-3" type="button" onClick={() => setOpen(false)}>
            <Icon icon="close" className="w-6 h-6" />
          </button>
          <div className="flex flex-col md:flex-row flex-wrap md:items-start gap-y-8 gap-x-12">
            <Input
              label={labels["events-filters-search"] ?? ""}
              placeholder={labels["events-filters-search"] ?? ""}
              className="flex-auto"
              type="text"
              {...register("search")}
            />
            <Select
              label={labels["events-filters-country"]}
              {...register("country")}
              className="flex-auto"
            >
              <option value="">{labels["events-filters-all-countries"]}</option>
              {countryOptions.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </Select>
            <EventTypeFilter
              labels={labels}
              value={eventType}
              onChange={(v) => setValue("eventType", v)}
              className="flex-none"
            />
            {!hideMaterialsFilter && (
              <MaterialsFormFields
                labels={labels}
                materialsToShow={Object.values(MaterialType)}
                className="flex-none"
              />
            )}
          </div>
          <div className="mt-8">
            <Button variant="primary" type="submit" size="sm">
              {labels["events-filters-submit-button"]}
            </Button>
          </div>
        </div>
      </CollapsiblePanel>
    </form>
  );
};
